import * as THREE from 'three';
import { createMatteMaterial, createGlowMaterial } from '../../../visual';
import type { ShipSkin } from '../skins/ShipSkin';
import { type EngineMount, type ShipBuildResult } from './common';

export function buildMinerHauler(
  parent: THREE.Object3D,
  skin: ShipSkin
): ShipBuildResult {
  const strobes: THREE.Mesh[] = [];

  buildMainHull(parent, skin);
  buildDrillNose(parent, skin);
  buildCockpitCab(parent, skin);
  buildCargoPods(parent, skin);
  buildStabilizers(parent, skin);
  const engines = buildEngines(parent, skin);
  buildStrobes(parent, skin, strobes);

  return { engines, strobes, collisionRadius: 2.6 };
}

function buildMainHull(parent: THREE.Object3D, skin: ShipSkin): void {
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.75 });
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.55
  });
  const darkMat = createMatteMaterial({ color: skin.hullDark, roughness: 0.8 });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.6 });

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.9, 1.05, 3.0),
    hullMat
  );
  body.position.set(0, 0.1, 0.1);

  const underBelly = new THREE.Mesh(
    new THREE.BoxGeometry(1.75, 0.45, 2.8),
    darkMat
  );
  underBelly.position.set(0, -0.55, 0.1);

  const backPlate = new THREE.Mesh(
    new THREE.BoxGeometry(1.95, 1.1, 0.12),
    primaryMat
  );
  backPlate.position.set(0, 0.1, 1.55);

  const frontPlate = new THREE.Mesh(
    new THREE.BoxGeometry(1.7, 0.9, 0.15),
    primaryMat
  );
  frontPlate.position.set(0, 0.08, -1.35);

  const sideStripeGeo = new THREE.BoxGeometry(0.05, 0.18, 2.7);
  const stripeMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.4,
    emissive: skin.accent,
    emissiveIntensity: 0.04
  });
  const stripeLeft = new THREE.Mesh(sideStripeGeo, stripeMat);
  stripeLeft.position.set(-0.98, 0.1, 0.1);
  const stripeRight = stripeLeft.clone();
  stripeRight.position.x = 0.98;

  const topRidge = new THREE.Mesh(
    new THREE.BoxGeometry(1.6, 0.18, 2.7),
    primaryMat
  );
  topRidge.position.set(0, 0.7, 0.1);

  const topRidgeTrim = new THREE.Mesh(
    new THREE.BoxGeometry(1.65, 0.05, 2.75),
    trimMat
  );
  topRidgeTrim.position.set(0, 0.83, 0.1);

  const ribGeo = new THREE.BoxGeometry(2.0, 0.1, 0.08);
  for (let i = 0; i < 4; i++) {
    const rib = new THREE.Mesh(ribGeo, trimMat);
    rib.position.set(0, 0.1, -1.0 + i * 0.75);
    parent.add(rib);
  }

  const underHatch = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.08, 0.8),
    primaryMat
  );
  underHatch.position.set(0, -0.82, 0.3);

  parent.add(
    body,
    underBelly,
    backPlate,
    frontPlate,
    stripeLeft,
    stripeRight,
    topRidge,
    topRidgeTrim,
    underHatch
  );
}

function buildDrillNose(parent: THREE.Object3D, skin: ShipSkin): void {
  const darkMat = createMatteMaterial({ color: skin.trim, roughness: 0.6 });
  const primaryDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const accentMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.4,
    emissive: skin.accent,
    emissiveIntensity: 0.06
  });
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.7 });

  const neck = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.7, 0.9, 20),
    hullMat
  );
  neck.rotation.x = Math.PI / 2;
  neck.scale.set(1.1, 1.1, 1);
  neck.position.set(0, 0.08, -1.85);

  const collar = new THREE.Mesh(
    new THREE.TorusGeometry(0.62, 0.08, 10, 24),
    primaryDark
  );
  collar.rotation.x = Math.PI / 2;
  collar.scale.set(1.15, 1.05, 1);
  collar.position.set(0, 0.08, -2.22);

  const drill = new THREE.Mesh(
    new THREE.ConeGeometry(0.48, 1.1, 6),
    primaryDark
  );
  drill.rotation.x = -Math.PI / 2;
  drill.position.set(0, 0.08, -2.92);

  const drillSpiral = new THREE.Mesh(
    new THREE.TorusGeometry(0.34, 0.05, 6, 20),
    darkMat
  );
  drillSpiral.rotation.y = Math.PI / 2;
  drillSpiral.position.set(0, 0.08, -2.7);

  const drillSpiral2 = new THREE.Mesh(
    new THREE.TorusGeometry(0.22, 0.045, 6, 18),
    darkMat
  );
  drillSpiral2.rotation.y = Math.PI / 2;
  drillSpiral2.position.set(0, 0.08, -3.1);

  const drillTip = new THREE.Mesh(
    new THREE.ConeGeometry(0.09, 0.28, 8),
    accentMat
  );
  drillTip.rotation.x = -Math.PI / 2;
  drillTip.position.set(0, 0.08, -3.56);

  const mandibleGeo = new THREE.BoxGeometry(0.12, 0.5, 1.2);
  const mandibleLeft = new THREE.Mesh(mandibleGeo, primaryDark);
  mandibleLeft.position.set(-0.7, 0.08, -2.4);
  mandibleLeft.rotation.y = 0.18;
  const mandibleRight = mandibleLeft.clone();
  mandibleRight.position.x = 0.7;
  mandibleRight.rotation.y = -0.18;

  const headlightGeo = new THREE.SphereGeometry(0.09, 12, 10);
  const headlightMat = createGlowMaterial(skin.accent, 1, true);
  const headlightLeft = new THREE.Mesh(headlightGeo, headlightMat);
  headlightLeft.position.set(-0.55, 0.25, -2.3);
  const headlightRight = headlightLeft.clone();
  headlightRight.position.x = 0.55;

  parent.add(
    neck,
    collar,
    drill,
    drillSpiral,
    drillSpiral2,
    drillTip,
    mandibleLeft,
    mandibleRight,
    headlightLeft,
    headlightRight
  );
}

function buildCockpitCab(parent: THREE.Object3D, skin: ShipSkin): void {
  const frameMat = createMatteMaterial({ color: skin.trim, roughness: 0.5 });
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.45
  });
  const canopyMat = createMatteMaterial({
    color: skin.cockpit,
    roughness: 0.18,
    emissive: skin.cockpitGlow,
    emissiveIntensity: 0.05
  });
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.7 });

  const cabBase = new THREE.Mesh(
    new THREE.BoxGeometry(1.05, 0.45, 1.1),
    hullMat
  );
  cabBase.position.set(0, 1.0, -0.55);

  const cabPrimary = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.08, 1.15),
    primaryMat
  );
  cabPrimary.position.set(0, 1.27, -0.55);

  const windshield = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 0.38, 0.05),
    canopyMat
  );
  windshield.position.set(0, 1.05, -1.08);
  windshield.rotation.x = -0.35;

  const sideWindowGeo = new THREE.BoxGeometry(0.04, 0.3, 0.7);
  const sideWindowLeft = new THREE.Mesh(sideWindowGeo, canopyMat);
  sideWindowLeft.position.set(-0.54, 1.05, -0.6);
  const sideWindowRight = sideWindowLeft.clone();
  sideWindowRight.position.x = 0.54;

  const cabFrame = new THREE.Mesh(
    new THREE.BoxGeometry(1.12, 0.05, 1.18),
    frameMat
  );
  cabFrame.position.set(0, 0.77, -0.55);

  const antennaBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.06, 0.1, 8),
    frameMat
  );
  antennaBase.position.set(0.3, 1.35, -0.2);
  const antenna = new THREE.Mesh(
    new THREE.CylinderGeometry(0.012, 0.018, 0.55, 6),
    frameMat
  );
  antenna.position.set(0.3, 1.67, -0.2);
  const antennaTip = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 10, 8),
    createGlowMaterial(skin.accent, 1, true)
  );
  antennaTip.position.set(0.3, 1.95, -0.2);

  parent.add(
    cabBase,
    cabPrimary,
    windshield,
    sideWindowLeft,
    sideWindowRight,
    cabFrame,
    antennaBase,
    antenna,
    antennaTip
  );
}

function buildCargoPods(parent: THREE.Object3D, skin: ShipSkin): void {
  const podMat = createMatteMaterial({ color: skin.primary, roughness: 0.5 });
  const podDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.65
  });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.6 });
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.7 });

  const mkPod = (sign: 1 | -1): void => {
    const pod = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.42, 2.1, 18),
      podMat
    );
    pod.rotation.x = Math.PI / 2;
    pod.position.set(sign * 1.28, -0.05, 0.2);

    const capFront = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.34, 0.18, 18),
      podDark
    );
    capFront.rotation.x = Math.PI / 2;
    capFront.position.set(sign * 1.28, -0.05, -0.94);

    const capRear = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.42, 0.1, 18),
      podDark
    );
    capRear.rotation.x = Math.PI / 2;
    capRear.position.set(sign * 1.28, -0.05, 1.3);

    const ringGeo = new THREE.TorusGeometry(0.43, 0.045, 8, 22);
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(ringGeo, trimMat);
      ring.rotation.y = Math.PI / 2;
      ring.position.set(sign * 1.28, -0.05, -0.6 + i * 0.6);
      parent.add(ring);
    }

    const labelPanel = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.26, 0.8),
      hullMat
    );
    labelPanel.position.set(sign * 1.7, -0.05, 0.1);

    const strutTop = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.08, 0.18),
      trimMat
    );
    strutTop.position.set(sign * 1.02, 0.25, -0.5);
    const strutBot = strutTop.clone();
    strutBot.position.set(sign * 1.02, 0.25, 0.7);

    parent.add(pod, capFront, capRear, labelPanel, strutTop, strutBot);
  };

  mkPod(1);
  mkPod(-1);
}

function buildStabilizers(parent: THREE.Object3D, skin: ShipSkin): void {
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.5
  });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.6 });

  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(0.95, 0.05);
  shape.lineTo(0.85, 0.55);
  shape.lineTo(0, 0.6);
  shape.closePath();

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.08,
    bevelEnabled: true,
    bevelSize: 0.02,
    bevelThickness: 0.02,
    bevelSegments: 2
  });
  geo.translate(-0.04, 0, -0.04);

  const mkFin = (sign: 1 | -1): void => {
    const fin = new THREE.Mesh(geo, primaryMat);
    fin.scale.x = sign;
    fin.rotation.z = sign * 0.5;
    fin.position.set(sign * 0.92, 0.65, 1.1);

    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(0.7, 0.05, 0.06),
      trimMat
    );
    trim.position.set(sign * 1.35, 1.1, 1.25);
    trim.rotation.z = sign * 0.5;

    parent.add(fin, trim);
  };

  mkFin(1);
  mkFin(-1);
}

function buildEngines(parent: THREE.Object3D, skin: ShipSkin): EngineMount[] {
  const housingMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.45
  });
  const housingDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.6
  });
  const innerMat = createMatteMaterial({ color: skin.hullDark, roughness: 0.8 });
  const rimMat = createMatteMaterial({ color: skin.trim, roughness: 0.55 });
  const coreMat = createGlowMaterial(skin.glow, 0.9, true);
  const plumeMat = createGlowMaterial(skin.plasma, 0.4, true);

  const mounts: EngineMount[] = [];

  const make = (x: number, scale: number, yOffset = 0): EngineMount => {
    const z = 1.68;
    const y = 0 + yOffset;

    const housing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.46 * scale, 0.52 * scale, 0.9, 22),
      housingMat
    );
    housing.rotation.x = Math.PI / 2;
    housing.position.set(x, y, z);

    const ringFront = new THREE.Mesh(
      new THREE.TorusGeometry(0.5 * scale, 0.045, 8, 24),
      housingDark
    );
    ringFront.position.set(x, y, z - 0.38);

    const ringMid = new THREE.Mesh(
      new THREE.TorusGeometry(0.51 * scale, 0.04, 8, 24),
      housingDark
    );
    ringMid.position.set(x, y, z);

    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(0.46 * scale, 0.07, 8, 26),
      rimMat
    );
    rim.position.set(x, y, z + 0.46);

    const throat = new THREE.Mesh(
      new THREE.CylinderGeometry(0.36 * scale, 0.4 * scale, 0.42, 20),
      innerMat
    );
    throat.rotation.x = Math.PI / 2;
    throat.position.set(x, y, z + 0.28);

    const core = new THREE.Mesh(
      new THREE.CircleGeometry(0.3 * scale, 24),
      coreMat
    );
    core.position.set(x, y, z + 0.5);

    const plumeGeo = new THREE.ConeGeometry(
      0.36 * scale,
      1.4 * scale,
      22,
      1,
      true
    );
    const plume = new THREE.Mesh(plumeGeo, plumeMat);
    plume.rotation.x = Math.PI / 2;
    plume.position.set(x, y, z + 1.1 * scale);

    parent.add(housing, ringFront, ringMid, rim, throat, core, plume);

    return {
      local: new THREE.Vector3(x, y, z + 0.56),
      plume,
      core
    };
  };

  mounts.push(make(-0.75, 0.95));
  mounts.push(make(0.75, 0.95));
  mounts.push(make(0, 0.75, 0.6));

  return mounts;
}

function buildStrobes(
  parent: THREE.Object3D,
  skin: ShipSkin,
  strobes: THREE.Mesh[]
): void {
  const strobeMat = createGlowMaterial(skin.strobe, 1, true);
  const geo = new THREE.SphereGeometry(0.07, 10, 8);

  const left = new THREE.Mesh(geo, strobeMat);
  left.position.set(-1.85, 0.3, 0.2);
  const right = new THREE.Mesh(geo, strobeMat);
  right.position.set(1.85, 0.3, 0.2);

  const belly = new THREE.Mesh(
    new THREE.SphereGeometry(0.06, 10, 8),
    createGlowMaterial(skin.glow, 1, true)
  );
  belly.position.set(0, -0.85, 0.6);

  parent.add(left, right, belly);
  strobes.push(left, right, belly);
}
